import styled from '@emotion/styled'
import { LucideProps } from 'lucide-react'
import Popover from 'components/core/Popover'
import 'react-day-picker/style.css'
import { JSXElementConstructor } from 'react'
import { formatDateTimeRangeString } from 'utils/date.utils'
import { StyledLabel } from './components/StyledLabel'
import { SPACING } from 'constants/spacing.constants'
import Column from 'components/core/Column'
import { DateTimeButton } from './components/DateTimeButton'
import { DatePickerValue } from './types/datePicker.types'
import { CalendarWithTime } from './components/CalendarWithTime'
import Typography from 'components/core/Typography'
import { RequiredInputLabel } from 'components/core/RequiredInputLabel'

const StyledDatePickerContainer = styled(Column)<{ isCellStyle: boolean }>`
    row-gap: ${SPACING.sm};

    ${(p) => p.isCellStyle && `height: 100%;`}
`

const StyledPopoverTrigger = styled.button`
    width: 100%;
`

const emptyFunc = () => undefined

type DatePickerProps = {
    isRequiredLabel?: boolean
    isEditable?: boolean
    isCellStyle?: boolean
    hasTime?: boolean
    label?: string
    value?: DatePickerValue
    error?: string
    placeholder?: string
    align?: 'center' | 'end' | 'start'
    defaultDateTimeLabelFormat?: string
    Icon?: JSXElementConstructor<LucideProps>
    ButtonComponent?: JSXElementConstructor<any>
    onChange: (date: DatePickerValue) => void
    onClose?: () => void
}

export const DatePicker = ({
    isRequiredLabel,
    isEditable,
    isCellStyle,
    hasTime,
    label,
    value,
    error,
    placeholder,
    align = 'center',
    Icon,
    ButtonComponent,
    onChange,
    onClose = emptyFunc,
}: DatePickerProps) => {
    const dateText = formatDateTimeRangeString({
        from: value,
        hasTime,
    })

    if (!isEditable) {
        return (
            <StyledDatePickerContainer isCellStyle={!!isCellStyle}>
                {label && <StyledLabel>{label}</StyledLabel>}
                <StyledPopoverTrigger>
                    <DateTimeButton
                        isDisabled
                        hasError={!!error}
                        placeholder={placeholder}
                        dateText={dateText}
                        Icon={Icon}
                        ButtonComponent={ButtonComponent}
                    />
                </StyledPopoverTrigger>
            </StyledDatePickerContainer>
        )
    }

    return (
        <StyledDatePickerContainer isCellStyle={!!isCellStyle}>
            {label && (
                <StyledLabel>
                    {label}
                    {isRequiredLabel && <RequiredInputLabel />}
                </StyledLabel>
            )}
            <Popover
                align={align}
                onClose={onClose}
                trigger={
                    <StyledPopoverTrigger>
                        <DateTimeButton
                            placeholder={placeholder}
                            dateText={dateText}
                            Icon={Icon}
                            ButtonComponent={ButtonComponent}
                        />
                    </StyledPopoverTrigger>
                }
            >
                {(closePopup) => (
                    <CalendarWithTime
                        hasConfirmButton={false}
                        hasTime={hasTime}
                        value={value}
                        onConfirm={closePopup}
                        onChange={onChange}
                    />
                )}
            </Popover>
            {!!error && (
                <Typography variant="caption" color="error">
                    {error}
                </Typography>
            )}
        </StyledDatePickerContainer>
    )
}
